// ============================================================================
// FilterChips — the faceted filter surface. One row of facet groups (muscle ·
// movement pattern · equipment · modality · level); within each, tappable
// pill chips toggle a single value for that facet (tap again to clear). On
// phones the chip rows scroll horizontally with no wrap so the filter stays
// one thumb-height tall; the active chip animates its coral fill via a shared
// layout pill. Counts come from the whole catalog so chips never flicker.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/cn";
import type { FacetKey, FacetMap, FilterState } from "./lib";
import { titleCase } from "./lib";

const FACET_ORDER: FacetKey[] = [
  "muscle",
  "movement_pattern",
  "equipment",
  "modality",
  "level",
];

interface FilterChipsProps {
  facets: FacetMap;
  filters: FilterState;
  onToggle: (key: FacetKey, value: string) => void;
}

export function FilterChips({ facets, filters, onToggle }: FilterChipsProps) {
  const { t } = useTranslation("exercises");

  return (
    <div className="space-y-4">
      {FACET_ORDER.map((key) => {
        const options = facets[key];
        if (!options || options.length === 0) return null;
        return (
          <div key={key}>
            <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
              {t(`facets.${key}`)}
            </p>
            <div
              role="group"
              aria-label={t(`facets.${key}`)}
              className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible"
            >
              {options.map((opt) => (
                <Chip
                  key={opt.value}
                  groupKey={key}
                  active={filters[key] === opt.value}
                  count={opt.count}
                  label={t(`values.${key}.${opt.value}`, titleCase(opt.value))}
                  onClick={() => onToggle(key, opt.value)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Chip({
  groupKey,
  active,
  count,
  label,
  onClick,
}: {
  groupKey: FacetKey;
  active: boolean;
  count: number;
  label: string;
  onClick: () => void;
}) {
  const reduce = useReducedMotion();
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "relative inline-flex shrink-0 items-center gap-1.5 rounded-pill px-3.5 py-2 font-display text-sm font-bold transition-colors duration-fast",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
        active
          ? "text-white"
          : "bg-surface text-body-muted ring-1 ring-inset ring-line-strong shadow-xs hover:bg-surface-sunken hover:text-body",
      )}
    >
      {active && (
        <motion.span
          layoutId={`chip-${groupKey}`}
          transition={
            reduce ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 36 }
          }
          className="absolute inset-0 -z-10 rounded-pill bg-grad-coral shadow-volt"
        />
      )}
      <span className="relative z-10">{label}</span>
      <span
        className={cn(
          "relative z-10 tabular-nums text-xs font-extrabold",
          active ? "text-white/80" : "text-body-faint",
        )}
      >
        {count}
      </span>
    </button>
  );
}
